import React from 'react';
import { 
  FiActivity, 
  FiCheckCircle, 
  FiStar, 
  FiHeart,
  FiArrowUp
} from 'react-icons/fi';
import LaborScoreTrendsChart from './LaborScoreTrendsChart';
import MemberSatisfactionTrendChart from './MemberSatisfactionTrendChart';
import ScoreDistributionChart from './ScoreDistributionChart';
import FinancialHealthRadarChart from './FinancialHealthRadarChart';
import StressReductionByCategoryChart from './StressReductionByCategoryChart';

interface MetricCardProps {
  title: string;
  value: string | number;
  change: string;
  target: string;
  icon: React.ReactNode;
  isPositive: boolean;
}

const MetricCard: React.FC<MetricCardProps> = ({ 
  title, 
  value, 
  change, 
  target, 
  icon, 
  isPositive 
}) => {
  return (
    <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
      <div className="flex items-start justify-between">
        <div className="flex-grow">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-slate-600 font-medium text-xs sm:text-sm">{title}</h3>
            <div className="bg-slate-100 p-1.5 sm:p-2 rounded-full">
              {icon}
            </div>
          </div>
          
          <div className="mb-3">
            <p className="text-2xl sm:text-3xl font-bold text-slate-800 mb-1">{value}</p>
            <div className="flex items-center space-x-2">
              <span className={`flex items-center text-xs sm:text-sm font-medium ${
                isPositive ? 'text-green-600' : 'text-red-600'
              }`}>
                <FiArrowUp className="mr-1" size={12} />
                {change}
              </span>
            </div>
          </div>
          
          <div className="pt-3 border-t border-slate-200">
            <p className="text-slate-500 text-xs sm:text-sm">
              Target: <span className="font-medium text-slate-700">{target}</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

interface ProgressBarProps {
  title: string;
  percentage: number;
}

const ProgressBar: React.FC<ProgressBarProps> = ({ title, percentage }) => {
  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <h5 className="text-slate-700 font-medium">{title}</h5>
        <span className="text-slate-600 text-sm">{percentage}%</span>
      </div>
      <div className="w-full bg-slate-200 rounded-full h-3">
        <div 
          className="bg-slate-800 h-3 rounded-full transition-all duration-300"
          style={{ width: `${percentage}%` }}
        ></div>
      </div>
    </div>
  );
};

interface ConsistencyDataRow {
  scoreRange: string;
  memberCount: number;
  avgImprovement: string;
  consistencyRate: string;
}

const ConsistencyAnalysisTable: React.FC = () => {
  const data: ConsistencyDataRow[] = [
    {
      scoreRange: '30-55',
      memberCount: 215,
      avgImprovement: '+12%',
      consistencyRate: '96.6%'
    },
    {
      scoreRange: '55-65',
      memberCount: 1850,
      avgImprovement: '+28%',
      consistencyRate: '98.9%'
    },
    {
      scoreRange: '65-75',
      memberCount: 6200,
      avgImprovement: '+45%',
      consistencyRate: '97.7%'
    },
    {
      scoreRange: '75-85',
      memberCount: 5415,
      avgImprovement: '+38%',
      consistencyRate: '97.4%'
    }
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
      <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-6">Score Consistency Analysis</h4>
      
      {/* Progress Bars */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-8">
        <ProgressBar title="6-12 Month Consistency" percentage={97.8} />
        <ProgressBar title="12-24 Month Consistency" percentage={96.9} />
        <ProgressBar title="Overall Consistency" percentage={97.6} />
      </div>

      {/* Data Table */}
      <div className="overflow-x-auto scrollbar-hide">
        <table className="w-full min-w-full">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="text-left py-2 sm:py-3 px-2 sm:px-4 font-medium text-slate-700 text-xs sm:text-sm">Score Range</th>
              <th className="text-left py-2 sm:py-3 px-2 sm:px-4 font-medium text-slate-700 text-xs sm:text-sm">Member Count</th>
              <th className="text-left py-2 sm:py-3 px-2 sm:px-4 font-medium text-slate-700 text-xs sm:text-sm">Avg Improvement</th>
              <th className="text-left py-2 sm:py-3 px-2 sm:px-4 font-medium text-slate-700 text-xs sm:text-sm">Consistency Rate</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, index) => (
              <tr key={index} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-3 sm:py-4 px-2 sm:px-4 font-medium text-slate-800 text-xs sm:text-sm">{row.scoreRange}</td>
                <td className="py-3 sm:py-4 px-2 sm:px-4 text-slate-600 text-xs sm:text-sm">{row.memberCount.toLocaleString()}</td>
                <td className="py-3 sm:py-4 px-2 sm:px-4 text-green-600 font-semibold text-xs sm:text-sm">{row.avgImprovement}</td>
                <td className="py-3 sm:py-4 px-2 sm:px-4 text-slate-600 text-xs sm:text-sm">{row.consistencyRate}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const FinancialHealth: React.FC = () => {
  const healthMetrics = [
    {
      title: 'Avg VeraScore',
      value: '67',
      change: '+5.2%',
      target: '70',
      icon: <FiActivity className="text-slate-600" size={20} />,
      isPositive: true
    },
    {
      title: 'Score Consistency',
      value: '97.6%',
      change: '+1.8%',
      target: '95%',
      icon: <FiCheckCircle className="text-slate-600" size={20} />,
      isPositive: true
    },
    {
      title: 'Member Satisfaction',
      value: '4.6/5.0',
      change: '+21.1%',
      target: '4.8/5.0',
      icon: <FiStar className="text-slate-600" size={20} />,
      isPositive: true
    },
    {
      title: 'Stress Reduction',
      value: '33%',
      change: '+15.4%',
      target: '40%',
      icon: <FiHeart className="text-slate-600" size={20} />,
      isPositive: true
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header Text */}
      <div>
        <h3 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Track member financial wellness and satisfaction improvements</h3>
        
        {/* Key Metrics Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {healthMetrics.map((metric, index) => (
            <MetricCard key={index} {...metric} />
          ))}
        </div>
      </div>

      {/* VeraScore Trends & Satisfaction Trend Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
          <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-4">VeraScore Trends</h4>
          <div className="h-96 sm:h-80">
            <LaborScoreTrendsChart />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
          <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Member Satisfaction Trend</h4>
          <div className="h-96 sm:h-80">
            <MemberSatisfactionTrendChart />
          </div>
        </div>
      </div>

      {/* Score Distribution & Financial Health Radar Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
          <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Score Distribution & Improvement</h4>
          <div className="h-96 sm:h-80">
            <ScoreDistributionChart />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
          <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Overall Financial Health Metrics</h4>
          <div className="h-96 sm:h-80">
            <FinancialHealthRadarChart />
          </div>
        </div>
      </div>

      {/* Stress Reduction by Category Chart */}
      <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
        <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Member Stress Reduction by Category</h4>
        <div className="h-96 sm:h-80">
          <StressReductionByCategoryChart />
        </div>
      </div>

      {/* Consistency Analysis */}
      <ConsistencyAnalysisTable />
    </div>
  );
};

export default FinancialHealth;